import { useState, useMemo } from 'react';
import moment, { Moment } from 'moment';
import { getNewEarliestDate } from '@/pages/intelligence/services/topcharts';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import { useDispatch, useSelector } from 'umi';
import { toDateTypeNor } from '@/pages/intelligence/utils/common';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import {
  releaseDateFormatter,
  parseReleaseDateByPlatformsJson,
  getBaseReleaseTimeByDateTypeByPlatforms,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/ReleaseDate/utils';
import { fetchMobileEarliestDateByMetricDate, getValueList } from './metricDate';

export default function useEarliestTime({
  entityType = 'mobile',
  basicInfo,
  appIds,
  dateType,
  topchartsState = {},
  platforms = [],
  isReleaseTimeByPlatforms = false,
  segmentValue = '',
  setEarliestPeriodCur,
}: // segment,
any) {
  const [earliestDay, setEarliestDay] = useState('');
  const [earliestPeriod, setEarliestPeriod] = useState('');
  const [earliestDayData, setEarliestDayData] = useState<any[]>([]);
  const [earliestPeriodData, setEarliestPeriodData] = useState<any[]>([]);
  const [earliestLoading, setEarliestLoading] = useState(false);

  const dispatch = useDispatch();
  const { formatMessage, locale } = useIntlExtend();
  const { earliestCurDay, earliestDate, isNeedEarliestUpdate } = useSelector((state: any) => ({
    earliestDate: state[DBI_TOPCHARTS]?.earliestDate,
    earliestCurDay: state[DBI_TOPCHARTS]?.earliestCurDay,
    isNeedEarliestUpdate: state[DBI_TOPCHARTS]?.isNeedEarliestUpdate,
  }));

  const getBaseReleaseTimeByDateType = ({
    dateType,
    baseReleaseMonth,
    baseReleaseWeek,
    baseReleaseTime,
  }: {
    dateType: string;
    baseReleaseMonth: string;
    baseReleaseWeek: string;
    baseReleaseTime: string;
  }) => {
    const valueMap: { [key: string]: string } = {
      month: baseReleaseMonth,
      week: baseReleaseWeek,
      day: baseReleaseTime,
    };
    return moment(valueMap[toDateTypeNor[dateType]] || baseReleaseTime).format('YYYY-MM-DD');
  };

  const hasEarliestLogicInCurrentEntityType = useMemo(
    () => ['mobile', 'pc', 'console', 'combine'].includes(entityType),
    [entityType],
  );

  // releaseDatesByPlatform-格式化
  const formattedReleaseDatesByPlatforms = useMemo(() => {
    const releaseDatesByPlatformValue = parseReleaseDateByPlatformsJson(
      topchartsState.releaseDatesByPlatform,
    );
    const listTmp =
      releaseDateFormatter?.(releaseDatesByPlatformValue, formatMessage) ||
      releaseDatesByPlatformValue;
    return Array.isArray(listTmp) ? listTmp : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, platforms, topchartsState]);

  const baseReleaseTimeValueByPlatforms = useMemo(
    () =>
      getBaseReleaseTimeByDateTypeByPlatforms(
        dateType,
        formattedReleaseDatesByPlatforms,
        platforms,
      ),
    [dateType, formattedReleaseDatesByPlatforms, platforms],
  );

  const baseReleaseTimeValue = useMemo(() => {
    const baseTime = topchartsState.baseReleaseTime
      ? moment(topchartsState.baseReleaseTime).format('YYYY-MM-DD')
      : '';
    return isReleaseTimeByPlatforms ? baseReleaseTimeValueByPlatforms : baseTime;
  }, [dateType, topchartsState, isReleaseTimeByPlatforms]);

  const baseReleaseTimeData = useMemo(() => {
    const formatDateValue = toDateTypeNor[dateType];
    let baseTimeValue = baseReleaseTimeValue;
    switch (formatDateValue) {
      case 'week':
        baseTimeValue = moment(baseTimeValue).startOf('week').format('YYYY-MM-DD');
        break;
      case 'month':
        baseTimeValue = `${moment(baseTimeValue).format('YYYY-MM')}-01`;
        break;
      case 'day':
      default:
        break;
    }
    return topchartsState.ifShowBaseReleaseTime ? baseTimeValue : '';
  }, [dateType, topchartsState, baseReleaseTimeValue]);

  const formattedEarliestDay = useMemo(
    () =>
      // 重新渲染后，state没有了，从model获取
      earliestDay || earliestCurDay, // formatCalendar(earliestDay, '', TIMETYPETOLY[dateType]);
    [dateType, earliestDay, earliestCurDay],
  );

  type EarliestDateType = {
    dataSources?: string[] | string;
    metrics?: any;
    regionType?: string;
    regions?: string[];
    dateType: string;
    segment?: string;
    device?: string;
    platform?: any;
    platforms?: any;
    earliesUrl?: string;
    entity_type?: string;
    unified_id?: string;
    region_type?: string;
    app_ids?: string[];
    data_sources?: string[];
    metric?: string[];
    signal?: AbortSignal;
  };
  const updateEarliestDateData = async (paramsPa: EarliestDateType) => {
    const {
      dataSources,
      metrics,
      regionType,
      regions,
      dateType,
      segment,
      device,
      platform,
      earliesUrl,
      platforms,
      signal,
    } = paramsPa;
    setEarliestDay('');
    setEarliestPeriod('');
    try {
      let params = {
        entity_type: entityType, // 体类型：mobile,pc,console
        app_ids: appIds || paramsPa.app_ids, // appid数组
        unified_id: basicInfo.id || paramsPa.unified_id || '',
        data_sources: dataSources || paramsPa.data_sources, // 数据源列表
        metric: metrics || paramsPa.metric, // 指标列表
        region_type: regionType || paramsPa.region_type, // market,region
        regions, // 地区国家数组,全球为global
      } as any;

      if (segment && segment !== '') {
        params = {
          ...params,
          segment,
        };
      }
      if (device && device?.length > 0) {
        params = {
          ...params,
          device,
        };
      }
      if (platform && platform?.length > 0) {
        params = {
          ...params,
          platform,
        };
      }
      if (platforms && platforms?.length > 0) {
        params = {
          ...params,
          platforms,
        };
      }
      setEarliestLoading(true);
      let dataRes = {} as any;
      if (
        isNeedEarliestUpdate &&
        basicInfo.id &&
        basicInfo.id !== '' &&
        params?.regions?.length > 0 &&
        !isEmpty(params?.metric) &&
        // eslint-disable-next-line no-prototype-builtins
        ((params.hasOwnProperty('app_ids') && params.app_ids?.length) ||
          // eslint-disable-next-line no-prototype-builtins
          (params.hasOwnProperty('unified_id') && params.unified_id !== ''))
      ) {
        if (entityType === 'mobile') {
          dataRes = await fetchMobileEarliestDateByMetricDate(params, signal);
        } else {
          dataRes = await getNewEarliestDate(params, earliesUrl, signal ? { signal } : undefined);
        }
        if (signal?.aborted) {
          setEarliestLoading(false);
          return;
        }
        dispatch({ type: `${DBI_TOPCHARTS}/setEarliestDate`, payload: dataRes });
      } else {
        dataRes = earliestDate;
        dispatch({ type: `${DBI_TOPCHARTS}/setIsNeedEarliestUpdate`, payload: true });
      }

      const earliestDayValue = dataRes?.data?.earliest_day || [];
      const earliestPeriodValue = dataRes?.data?.earliest_period || [];
      setEarliestDayData(earliestDayValue);
      setEarliestPeriodData(earliestPeriodValue);

      let earliestDayList = [...earliestDayValue];
      if (segmentValue !== '') {
        earliestDayList = [...earliestDayValue]?.filter(
          (item: any) => item.source === segmentValue,
        );
      }

      if (earliestDayList?.length === 0) {
        dispatch({ type: `${DBI_TOPCHARTS}/setEarliestCurDay`, payload: '' });
        setEarliestDay('');
        setEarliestPeriodCur?.('');
        setEarliestPeriod('');
      } else {
        for (const n in earliestDayList) {
          if (toDateTypeNor[dateType] === toDateTypeNor[earliestDayList[n]?.period]) {
            dispatch({
              type: `${DBI_TOPCHARTS}/setEarliestCurDay`,
              payload: earliestDayList[n]?.date || '',
            });
            setEarliestDay(earliestDayList[n]?.date || '');
            setEarliestPeriodCur?.(earliestDayList[n]?.date || '');
            setEarliestPeriod(earliestPeriodValue[n]?.date || '');
          }
        }
      }

      setEarliestLoading(false);
    } catch (exception) {
      console.warn(exception);
      setEarliestDay('');
      setEarliestPeriod('');
      setEarliestDayData([]);
      setEarliestPeriodData([]);
      setEarliestLoading(false);
    }
  };

  const getEarliestDayAndEarliesPeriod = (
    type: string,
    metric: string | string[],
  ): { earliestDay: string; earliestPeriod: string } => {
    if (!type || !Array.isArray(earliestDayData)) {
      return {
        earliestDay: '',
        earliestPeriod: '',
      };
    }

    let earliestDay = '';
    let earliestPeriod = '';

    try {
      for (const n in earliestDayData) {
        if (
          getValueList(metric).includes(earliestDayData[n]?.metric) &&
          toDateTypeNor[type] == toDateTypeNor[earliestDayData[n]?.period]
        ) {
          earliestDay = earliestDayData[n]?.date || '';
          earliestPeriod = earliestPeriodData[n]?.date || '';
        }
      }
    } catch (exception) {
      console.warn(exception);
      earliestDay = '';
      earliestPeriod = '';
    }

    return {
      earliestDay,
      earliestPeriod,
    };
  };

  const getStartDateBySidebarName = ({
    sidebarName,
    earliestDay,
    type,
    startDate,
    baseReleaseMonth,
    baseReleaseWeek,
    baseReleaseTime,
  }: {
    sidebarName: string;
    earliestDay: string;
    type: string;
    startDate: string;
    baseReleaseMonth: string;
    baseReleaseWeek: string;
    baseReleaseTime: string;
  }): string => {
    const startDateGetters: { [key: string]: () => string } = {
      'Earliest Data': () => earliestDay,
      'Since Release': () =>
        getBaseReleaseTimeByDateType({
          dateType: type,
          baseReleaseMonth,
          baseReleaseWeek,
          baseReleaseTime,
        }),
      // 'All of Time': () => '1970-01-01',
    };

    const startDateDefaultGetter = () => startDate;

    const startDateGetter = startDateGetters[sidebarName] || startDateDefaultGetter;

    return startDateGetter();
  };

  /**
   * 根据接口返回的折线图数据，获取 trends、country、earliestDate
   * trends 处理过日期格式（YYYY-MM-DD）的数据、
   * country 最早有数据的国家名、
   * earliestDate 最早有数据的日期
   *
   * @param trends 接口返回的折线图数据
   * @returns trends 处理过日期格式（YYYY-MM-DD）的数据、country 最早有数据的国家名、earliestDate 最早有数据的日期
   */
  const getNewTrendsDataAndEarliestDataCountry = (
    trends: any[],
  ): {
    trends: any[];
    country: string;
    earliestDate: string;
  } => {
    let _trends: any[] = [];
    let _trend: any = null;
    let _histories: any[] = [];
    let _history: any = null;
    let _date: Moment | null;
    let _earliestDate: Moment | null = moment();
    let _country = '';

    try {
      for (let i = 0; i < trends?.length; i++) {
        _trend = trends[i];
        if (!_trend || !Array.isArray(_trend.history)) continue;
        _histories = [];
        for (let j = 0; j < _trend.history?.length; j++) {
          _history = _trend.history[j];
          if (!_history) continue;
          _date = moment(_history.date);
          _history = {
            ..._history,
            date: _date.format('YYYY-MM-DD'),
          };
          _histories.push(_history);

          if (_country !== _trend.name) {
            if (_date.isBefore(_earliestDate) && _history.value) {
              _country = _trend.name;
              _earliestDate = _date;
            }
          }
        }
        _trend = {
          ..._trend,
          history: _histories,
        };
        _trends.push(_trend);
      }
    } catch (exception) {
      console.warn(exception);
      _trends = [];
      _country = '';
      _earliestDate = null;
    }

    return {
      trends: _trends,
      country: _country,
      earliestDate: _earliestDate ? _earliestDate.format('YYYY-MM-DD') : '',
    };
  };

  const getSerieWithMarkLine = ({
    index,
    data,
    lineData,
    showReleaseDate,
    showEarliestDate,
    baseReleaseTime,
    earliestPeriod,
    earliestCountry,
  }: {
    index: number;
    data: any;
    lineData: any[];
    showReleaseDate: boolean;
    showEarliestDate: boolean;
    baseReleaseTime: string;
    earliestPeriod: string;
    earliestCountry: string;
  }): any => {
    let serie = {
      name: data?.name === 'global' || data?.market === 'global' ? 'Global' : data?.name,
      type: 'line',
      data: lineData,
    };
    const releaseDateMarkLineData: any = {
      name: 'Since Release',
      xAxis: baseReleaseTime,
      label: {
        position: 'end',
        show: true,
        textStyle: {
          color: '#0053B2',
          fontSize: 12,
          opacity: 0.54,
        },
        padding: [0, 0, -30, 0],
        formatter: (item: any): string => item?.name || '',
      },
    };
    const earliestDateMarkLineData: any = {
      name: 'Earliest Data',
      xAxis: earliestPeriod,
      label: {
        position: 'end',
        show: true,
        textStyle: {
          color: '#0053B2',
          fontSize: 12,
        },
        padding: [0, 0, 2, 0],
        formatter: (item: any): string => item?.name || '',
      },
    };
    const markLine = {
      symbol: ['none', 'none'],
      itemStyle: {
        normal: {
          lineStyle: {
            type: 'dashed',
            color: '#0053B2',
            opacity: 0.54,
          },
        },
      },
      data: [],
    } as any;

    if (showReleaseDate && index === 0) {
      markLine.data?.push(releaseDateMarkLineData);
    }
    if (showEarliestDate && earliestCountry === data?.name) {
      markLine.data?.push(earliestDateMarkLineData);
    }
    if ((showReleaseDate || showEarliestDate) && markLine.data?.length) {
      serie = { ...serie, markLine };
    }
    return serie;
  };

  const getShortcuttedLineSeries = (lineSeries: any[], shortcutIndex: number): any[] => {
    let result;
    try {
      result = lineSeries?.map((item: any) => {
        const newData = item.data?.slice(shortcutIndex);
        return { ...item, data: newData };
      });
    } catch (exception) {
      console.warn(exception);
      result = lineSeries;
    }
    return result;
  };

  return {
    hasEarliestLogicInCurrentEntityType,
    earliestDay,
    setEarliestDay,
    earliestPeriod,
    setEarliestPeriod,
    earliestDayData,
    setEarliestDayData,
    earliestPeriodData,
    setEarliestPeriodData,
    earliestLoading,
    formattedEarliestDay,
    getBaseReleaseTimeByDateType,
    getEarliestDayAndEarliesPeriod,
    getStartDateBySidebarName,
    getNewTrendsDataAndEarliestDataCountry,
    getSerieWithMarkLine,
    getShortcuttedLineSeries,
    updateEarliestDateData,
    baseReleaseTimeData,
    baseReleaseTimeValue,
  };
}
